class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick =
      this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm =
      this.querySelector('form');

    if (facetForm) {
      facetForm.addEventListener(
        'input',
        this.debouncedOnSubmit.bind(this)
      );
    }

    const facetWrapper =
      this.querySelector(
        '#FacetsWrapperDesktop'
      );

    if (facetWrapper) {
      facetWrapper.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (
        searchParams ===
        FacetFiltersForm.searchParamsPrev
      ) {
        return;
      }

      FacetFiltersForm.renderPage(
        searchParams,
        null,
        false
      );
    };

    window.addEventListener(
      'popstate',
      onHistoryChange
    );
  }

  static toggleActiveFacets(
    disable = true
  ) {
    document
      .querySelectorAll(
        '.js-facet-remove'
      )
      .forEach((element) => {
        element.classList.toggle(
          'disabled',
          disable
        );
      });
  }

  static isSearchResultsPage() {
    return Boolean(
      document.querySelector(
        '[data-search-products-root]'
      )
    );
  }

  static normalizeSearchParams(
    searchParams
  ) {
    if (
      !FacetFiltersForm.isSearchResultsPage()
    ) {
      return searchParams;
    }

    const params =
      new URLSearchParams(
        searchParams || ''
      );

    const currentParams =
      new URLSearchParams(
        window.location.search
      );

    const currentSortOrder =
      currentParams.get('sort_order');

    params.set('type', 'product');
    params.delete('sort_by');
    params.delete('page');
    params.delete('search_page');

    if (
      !params.has('sort_order') &&
      currentSortOrder
    ) {
      params.set(
        'sort_order',
        currentSortOrder
      );
    }

    return params.toString();
  }

  static setLoadingState(isLoading) {
    const gridCollection =
      document.querySelector(
        '#ProductGridContainer .collection'
      );

    gridCollection?.classList.toggle(
      'loading',
      isLoading
    );

    document
      .querySelectorAll(
        '#ProductCount, #ProductCountDesktop'
      )
      .forEach((element) => {
        element.classList.toggle(
          'loading',
          isLoading
        );
      });

    document
      .querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      )
      .forEach((spinner) => {
        spinner.classList.toggle(
          'hidden',
          !isLoading
        );
      });
  }

  static initializeScrollAnimations() {
    /*
      On the search results page, search-newest.js manages
      product card visibility, so Dawn's scroll animations
      are not reinitialized.
    */
    if (
      FacetFiltersForm.isSearchResultsPage()
    ) {
      return;
    }

    if (
      typeof initializeScrollAnimationTrigger ===
      'function'
    ) {
      initializeScrollAnimationTrigger();
    }
  }

  static handleRenderError(error) {
    console.error(
      '[Facets] An error occurred while updating the filtered results.',
      error
    );

    FacetFiltersForm.setLoadingState(
      false
    );

    FacetFiltersForm.toggleActiveFacets(
      false
    );
  }

  static renderPage(
    searchParams,
    event,
    updateURLHash = true
  ) {
    searchParams =
      FacetFiltersForm.normalizeSearchParams(
        searchParams
      );

    FacetFiltersForm.searchParamsPrev =
      searchParams;

    const sections =
      FacetFiltersForm.getSections();

    FacetFiltersForm.setLoadingState(
      true
    );

    if (sections.length === 0) {
      FacetFiltersForm.setLoadingState(
        false
      );

      return;
    }

    sections.forEach((section) => {
      const url =
        `${window.location.pathname}` +
        `?section_id=${section.section}` +
        `&${searchParams}`;

      const filterDataUrl = (element) =>
        element.url === url;

      if (
        FacetFiltersForm.filterData.some(
          filterDataUrl
        )
      ) {
        FacetFiltersForm.renderSectionFromCache(
          filterDataUrl,
          event
        );
      } else {
        FacetFiltersForm.renderSectionFromFetch(
          url,
          event
        );
      }
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(
        searchParams
      );
    }
  }

  static renderSectionFromFetch(
    url,
    event
  ) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Unable to load the filtered results. (${response.status})`
          );
        }

        return response.text();
      })
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];

        FacetFiltersForm.renderFilters(
          html,
          event
        );

        FacetFiltersForm.renderProductGridContainer(
          html
        );

        FacetFiltersForm.renderProductCount(
          html
        );

        FacetFiltersForm.initializeScrollAnimations();
      })
      .catch((error) => {
        FacetFiltersForm.handleRenderError(
          error
        );
      });
  }

  static renderSectionFromCache(
    filterDataUrl,
    event
  ) {
    try {
      const cachedEntry =
        FacetFiltersForm.filterData.find(
          filterDataUrl
        );

      if (!cachedEntry) {
        throw new Error(
          'The cached filtered results could not be found.'
        );
      }

      const html =
        cachedEntry.html;

      FacetFiltersForm.renderFilters(
        html,
        event
      );

      FacetFiltersForm.renderProductGridContainer(
        html
      );

      FacetFiltersForm.renderProductCount(
        html
      );

      FacetFiltersForm.initializeScrollAnimations();
    } catch (error) {
      FacetFiltersForm.handleRenderError(
        error
      );
    }
  }

  static renderProductGridContainer(
    html
  ) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const nextContainer =
      parsedHTML.getElementById(
        'ProductGridContainer'
      );

    const currentContainer =
      document.getElementById(
        'ProductGridContainer'
      );

    if (
      !nextContainer ||
      !currentContainer
    ) {
      return;
    }

    currentContainer.innerHTML =
      nextContainer.innerHTML;

    /*
      Product card visibility fixes and the custom sorting
      refresh event run only on the search results page.

      Collection pages keep Dawn's original product card
      animations and sorting behavior.
    */
    if (
      FacetFiltersForm.isSearchResultsPage()
    ) {
      currentContainer
        .querySelectorAll(
          '.scroll-trigger'
        )
        .forEach((element) => {
          element.classList.remove(
            'scroll-trigger--offscreen'
          );

          element.classList.add(
            'scroll-trigger--cancel'
          );

          element.style.removeProperty(
            'opacity'
          );

          element.style.removeProperty(
            'transform'
          );
        });

      const productGrid =
        currentContainer.querySelector(
          '#product-grid'
        );

      document.dispatchEvent(
        new CustomEvent(
          'search:product-grid-updated',
          {
            detail: {
              sectionId:
                productGrid?.dataset.id ||
                null,
            },
          }
        )
      );
    }
  }

  static renderProductCount(html) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const sourceCount =
      parsedHTML.getElementById(
        'ProductCount'
      );

    const container =
      document.getElementById(
        'ProductCount'
      );

    const containerDesktop =
      document.getElementById(
        'ProductCountDesktop'
      );

    if (
      sourceCount &&
      container
    ) {
      container.innerHTML =
        sourceCount.innerHTML;
    }

    if (
      sourceCount &&
      containerDesktop
    ) {
      containerDesktop.innerHTML =
        sourceCount.innerHTML;
    }

    FacetFiltersForm.setLoadingState(
      false
    );
  }

  static renderFilters(html, event) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const facetDetailsElementsFromFetch =
      parsedHTML.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

    const facetDetailsElementsFromDom =
      document.querySelectorAll(
        '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
      );

    /*
      Remove filters that are no longer returned by the server.
    */
    Array.from(
      facetDetailsElementsFromDom
    ).forEach((currentElement) => {
      const existsInFetchedHTML =
        Array.from(
          facetDetailsElementsFromFetch
        ).some(({ id }) => {
          return (
            currentElement.id === id
          );
        });

      if (!existsInFetchedHTML) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter =
        event?.target?.closest(
          '.js-filter'
        );

      return jsFilter
        ? element.id === jsFilter.id
        : false;
    };

    const facetsToRender =
      Array.from(
        facetDetailsElementsFromFetch
      ).filter((element) => {
        return !matchesId(element);
      });

    const countsToRender =
      Array.from(
        facetDetailsElementsFromFetch
      ).find(matchesId);

    facetsToRender.forEach(
      (elementToRender, index) => {
        const currentElement =
          document.getElementById(
            elementToRender.id
          );

        if (currentElement) {
          currentElement.innerHTML =
            elementToRender.innerHTML;

          return;
        }

        if (index > 0) {
          const {
            className:
              previousElementClassName,
            id: previousElementId,
          } = facetsToRender[
            index - 1
          ];

          if (
            elementToRender.className ===
            previousElementClassName
          ) {
            const previousElement =
              document.getElementById(
                previousElementId
              );

            if (previousElement) {
              previousElement.after(
                elementToRender
              );

              return;
            }
          }
        }

        if (
          elementToRender.parentElement
        ) {
          const firstFilter =
            document.querySelector(
              `#${elementToRender.parentElement.id} .js-filter`
            );

          firstFilter?.before(
            elementToRender
          );
        }
      }
    );

    FacetFiltersForm.renderActiveFacets(
      parsedHTML
    );

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML
    );

    if (
      countsToRender &&
      event?.target
    ) {
      const currentFilter =
        event.target.closest(
          '.js-filter'
        );

      const closestJSFilterID =
        currentFilter?.id;

      if (
        closestJSFilterID &&
        currentFilter
      ) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          currentFilter
        );

        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(
            closestJSFilterID
          )
        );

        const newFacetDetailsElement =
          document.getElementById(
            closestJSFilterID
          );

        if (!newFacetDetailsElement) {
          return;
        }

        const newElementSelector =
          newFacetDetailsElement
            .classList
            .contains(
              'mobile-facets__details'
            )
            ? '.mobile-facets__close-button'
            : '.facets__summary';

        const newElementToActivate =
          newFacetDetailsElement
            .querySelector(
              newElementSelector
            );

        const isTextInput =
          event.target.getAttribute(
            'type'
          ) === 'text';

        if (
          newElementToActivate &&
          !isTextInput
        ) {
          newElementToActivate.focus();
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach(
      (selector) => {
        const activeFacetsElement =
          html.querySelector(selector);

        if (!activeFacetsElement) {
          return;
        }

        const targetActiveFacets =
          document.querySelector(
            selector
          );

        if (targetActiveFacets) {
          targetActiveFacets.innerHTML =
            activeFacetsElement.innerHTML;
        }
      }
    );

    FacetFiltersForm.toggleActiveFacets(
      false
    );
  }

  static renderAdditionalElements(
    html
  ) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach(
      (selector) => {
        const sourceElement =
          html.querySelector(selector);

        const targetElement =
          document.querySelector(
            selector
          );

        if (
          !sourceElement ||
          !targetElement
        ) {
          return;
        }

        targetElement.innerHTML =
          sourceElement.innerHTML;
      }
    );

    const mobileFacetForm =
      document.getElementById(
        'FacetFiltersFormMobile'
      );

    const menuDrawer =
      mobileFacetForm?.closest(
        'menu-drawer'
      );

    if (
      menuDrawer &&
      typeof menuDrawer.bindEvents ===
        'function'
    ) {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(
    source,
    target
  ) {
    if (!source || !target) {
      return;
    }

    const targetSummary =
      target.querySelector(
        '.facets__summary'
      );

    const sourceSummary =
      source.querySelector(
        '.facets__summary'
      );

    if (
      sourceSummary &&
      targetSummary
    ) {
      targetSummary.outerHTML =
        sourceSummary.outerHTML;
    }

    const targetHeaderElement =
      target.querySelector(
        '.facets__header'
      );

    const sourceHeaderElement =
      source.querySelector(
        '.facets__header'
      );

    if (
      sourceHeaderElement &&
      targetHeaderElement
    ) {
      targetHeaderElement.outerHTML =
        sourceHeaderElement.outerHTML;
    }

    const targetWrapElement =
      target.querySelector(
        '.facets-wrap'
      );

    const sourceWrapElement =
      source.querySelector(
        '.facets-wrap'
      );

    if (
      sourceWrapElement &&
      targetWrapElement
    ) {
      const isShowingMore = Boolean(
        target.querySelector(
          'show-more-button .label-show-more.hidden'
        )
      );

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(
            '.facets__item.hidden'
          )
          .forEach((hiddenItem) => {
            hiddenItem.classList.replace(
              'hidden',
              'show-more-item'
            );
          });
      }

      targetWrapElement.outerHTML =
        sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(
    source,
    target
  ) {
    if (!source || !target) {
      return;
    }

    const targetFacetsList =
      target.querySelector(
        '.mobile-facets__list'
      );

    const sourceFacetsList =
      source.querySelector(
        '.mobile-facets__list'
      );

    if (
      sourceFacetsList &&
      targetFacetsList
    ) {
      targetFacetsList.outerHTML =
        sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(
    searchParams
  ) {
    history.pushState(
      {
        searchParams,
      },
      '',
      `${window.location.pathname}${
        searchParams
          ? `?${searchParams}`
          : ''
      }`
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid'
      );

    if (!productGrid?.dataset.id) {
      return [];
    }

    return [
      {
        section:
          productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    if (!form) {
      return '';
    }

    const formData =
      new FormData(form);

    return new URLSearchParams(
      formData
    ).toString();
  }

  onSubmitForm(
    searchParams,
    event
  ) {
    FacetFiltersForm.renderPage(
      searchParams,
      event
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const targetForm =
      event.target.closest('form');

    if (!targetForm) {
      return;
    }

    const sortFilterForms =
      document.querySelectorAll(
        'facet-filters-form form'
      );

    if (
      event.target.classList.contains(
        'mobile-facets__checkbox'
      )
    ) {
      const searchParams =
        this.createSearchParams(
          targetForm
        );

      this.onSubmitForm(
        searchParams,
        event
      );

      return;
    }

    const forms = [];

    const isMobile =
      targetForm.id ===
      'FacetFiltersFormMobile';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (
          form.id ===
            'FacetSortForm' ||
          form.id ===
            'FacetFiltersForm' ||
          form.id ===
            'FacetSortDrawerForm'
        ) {
          forms.push(
            this.createSearchParams(
              form
            )
          );
        }

        return;
      }

      if (
        form.id ===
        'FacetFiltersFormMobile'
      ) {
        forms.push(
          this.createSearchParams(
            form
          )
        );
      }
    });

    this.onSubmitForm(
      forms
        .filter(Boolean)
        .join('&'),
      event
    );
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const currentHref =
      event.currentTarget?.href || '';

    const queryIndex =
      currentHref.indexOf('?');

    const url =
      queryIndex === -1
        ? ''
        : currentHref.slice(
            queryIndex + 1
          );

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

if (
  !customElements.get(
    'facet-filters-form'
  )
) {
  customElements.define(
    'facet-filters-form',
    FacetFiltersForm
  );

  FacetFiltersForm.setListeners();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll(
      'input'
    ).forEach((element) => {
      element.addEventListener(
        'change',
        this.onRangeChange.bind(this)
      );

      element.addEventListener(
        'keydown',
        this.onKeyDown.bind(this)
      );
    });

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(
      event.currentTarget
    );

    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) {
      return;
    }

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;

    if (!event.key.match(pattern)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs =
      this.querySelectorAll('input');

    const minInput =
      inputs[0];

    const maxInput =
      inputs[1];

    if (!minInput || !maxInput) {
      return;
    }

    if (maxInput.value) {
      minInput.setAttribute(
        'data-max',
        maxInput.value
      );
    }

    if (minInput.value) {
      maxInput.setAttribute(
        'data-min',
        minInput.value
      );
    }

    if (minInput.value === '') {
      maxInput.setAttribute(
        'data-min',
        0
      );
    }

    if (maxInput.value === '') {
      minInput.setAttribute(
        'data-max',
        maxInput.getAttribute(
          'data-max'
        )
      );
    }
  }

  adjustToValidValues(input) {
    const value =
      Number(input.value);

    const min =
      Number(
        input.getAttribute(
          'data-min'
        )
      );

    const max =
      Number(
        input.getAttribute(
          'data-max'
        )
      );

    if (value < min) {
      input.value = min;
    }

    if (value > max) {
      input.value = max;
    }
  }
}

if (
  !customElements.get('price-range')
) {
  customElements.define(
    'price-range',
    PriceRange
  );
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const facetLink =
      this.querySelector('a');

    if (!facetLink) {
      return;
    }

    facetLink.setAttribute(
      'role',
      'button'
    );

    facetLink.addEventListener(
      'click',
      this.closeFilter.bind(this)
    );

    facetLink.addEventListener(
      'keyup',
      (event) => {
        event.preventDefault();

        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          this.closeFilter(event);
        }
      }
    );
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      this.closest(
        'facet-filters-form'
      ) ||
      document.querySelector(
        'facet-filters-form'
      );

    if (
      form &&
      typeof form.onActiveFilterClick ===
        'function'
    ) {
      form.onActiveFilterClick(
        event
      );
    }
  }
}

if (
  !customElements.get('facet-remove')
) {
  customElements.define(
    'facet-remove',
    FacetRemove
  );
}